<template>
  <section>
    <PageTitle>Visiting Record</PageTitle>
    <PageContent>
      <form @submit.prevent="submit" novalidate>
        <Container>
          <NoticeBox type="error" v-if="hasErrors">
            Please correct the specified fields
          </NoticeBox>

          <VisitCustomer
            :name="visit.customer_name"
            :email="visit.email"
            :cmc-no="visit.cmc_no"
          />

          <section class="section">
            <Headline :level="2">Visiting details</Headline>
            <div class="form-group required">
              <label>Visit Date 来店日</label>
              <Datepicker 
                v-model="visit.use_datetime"
                :class="{ 'has-error': hasError('visit.use_datetime') }"/>
              <FormError :errors="errors['visit.use_datetime'] || []" />
            </div>
            <div class="form-group required">
              <label>Venue 利用店舗</label>
              <div class="select is-fullwidth">
                <select
                  class="form-control"
                  :class="{ 'has-error': hasError('visit.use_facility_id') }"
                  v-model.number="visit.use_facility_id"
                  @change="onFacilityChange"
                >
                  <option value="0">Select a venue</option>
                  <option
                    v-for="item in facilities"
                    :key="item.use_facility_id"
                    :value="item.use_facility_id"
                    v-html="item.use_facility_name"
                  />
                </select>
              </div>
              <FormError :errors="errors['visit.use_facility_id'] || []" />
              <template v-if="visit.use_facility_id !== 0">
                <div class="select space-t-2 is-fullwidth">
                  <select
                    class="form-control"
                    :class="{ 'has-error': hasError('visit.use_child_facility_id') }"
                    v-model.number="visit.use_child_facility_id"
                    :disabled="isFacilityLoading"
                  >
                    <option value="0">Select a place</option>
                    <option
                      v-for="item in childFacilities"
                      :key="item.use_child_facility_id"
                      :value="item.use_child_facility_id"
                      v-html="item.use_child_facility_name"
                    />
                  </select>
                </div>
                <FormError :errors="errors['visit.use_child_facility_id'] || []" />
              </template>
            </div>
            <div class="form-group">
              <label>Number of guests ご利用人数</label>
              <div class="select block">
                <select
                  class="form-control"
                  :class="{ 'has-error': hasError('visit.count_of_people') }"
                  v-model.number="visit.count_of_people"
                >
                  <option value="0">Select a number</option>
                  <option v-for="n in 10" :value="n" :key="n">
                    {{ n }}
                  </option>
                  <option :value="11">more than 11</option>
                </select>
              </div>
              <FormError :errors="errors['visit.count_of_people'] || []" />
            </div>
            <div class="form-group">
              <label>Amount paid お支払金額</label>
              <input
                id="bill_amount"
                type="number"
                class="form-control"
                :class="{ 'has-error': hasError('visit.bill_amount') }"
                v-model.number="visit.bill_amount"
                placeholder="Enter a number"
                pattern="\d*"
              />
              <FormError :errors="errors['visit.bill_amount'] || []" />
            </div>
            <div class="form-group">
              <label>Purpose ご利用用途</label>
              <div class="select block">
                <select
                  class="form-control"
                  :class="{ 'has-error': hasError('visit.use_purpose_id') }"
                  v-model.number="visit.use_purpose_id"
                >
                  <option value="0">Select a purpose</option>
                  <option
                    v-for="data in usePurpose"
                    :value="data.use_purpose_id"
                    :key="data.use_purpose_id"
                    v-html="data.use_purpose_name"
                  />
                </select>
              </div>
              <FormError :errors="errors['visit.use_purpose_id'] || []" />
            </div>
            <div class="form-group">
                <label>Remarks 備考</label>
                <textarea
                  class="form-control is-multiline"
                  placeholder="Enter text"
                  v-model="visit.remarks"
                  maxlength="1000"
                  :rows="visit.remarks.split(/\n/).length"
                />
            </div>
            <div class="form-footer">
              <div class="text-center">
                <WaveButton type="submit" class="button" :loading="loading">
                  Save
                </WaveButton>
              </div>
            </div>
          </section>
        </Container>
      </form>

      <Modal
        id="complate-update"
        title="Visiting record was saved"
        icon="check"
        @ok="modalOk"
      />
    </PageContent>
  </section>
</template>

<script>
import withEmpty from '@/filters/with-empty'
import Modal from '@/components/Modal.vue'
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import VisitCustomer from '@/components/VisitCustomer.vue'
import usePurposeApi from '@/api/use-purpose'
import facilityApi from '@/api/use-facility'
import receptionistApi from '@/api/receptionist'
import visitApi from '@/api/visit'
import memberApi from '@/api/member'
import { renderError, visitTokenMismatch, isNotFound } from '@/utils/error'
import validationMixin from '@/mixins/validation'
import Datepicker from '@/components/Datepicker.vue'
import moment from 'moment'

export default {
  components: {
    Modal,
    NoticeBox,
    FormError,
    VisitCustomer,
    Datepicker
  },

  mixins: [
    validationMixin
  ],

  rules: {
    'visit.use_datetime': ['来店日', [
      ['required', '', 'Please enter the visit date']
    ]],
    'visit.use_facility_id': ['利用店舗', [
      ['required', { deny: [0] }, 'Please enter the venue']
    ]],
    'visit.use_child_facility_id': ['利用施設', [
      ['required', { deny: [0] }, 'Please enter the place']
    ]],
    'visit.bill_amount': ['お支払い金額（割引後）', [
      ['digits', 9, 'Please enter the discounted amount within 9 digits']
    ]],
  },

  data() {
    return {
      visit: {
        use_facility_id: 0,
        use_child_facility_id: 0,
        use_purpose_id: 0,
        count_of_people: 0,
        bill_amount: null,
        revision: '',
        use_datetime: '',
        email: '',
        customer_name: '',
        cmc_no: '',
        remarks: '',
      },
      usePurpose: {
        use_purpose_id: '',
        use_purpose_name: ''
      },
      facilities: [],
      childFacilities: [],
      token: '',
      loading: false,
      historyId: 0,
      isFacilityLoading: false
    }
  },

  metaInfo() {
    return {
      title: this.$store.state.app.pageName,
      bodyAttrs: { class: 'visit' }
    }
  },

  filters: {
    withEmpty
  },

  computed: {
    userId() {
      return this.$route.query.id
    }
  },

  methods: {
    async fetchVisit() {
      try {
        this.visit = await visitApi.show(this.userId, 0)
      } catch(err) {
        renderError(err)
      }
    },
    async fetchUsePurpose() {
      try {
        this.usePurpose = await usePurposeApi.get()
      } catch(err) {
        renderError(err)
      }
    },
    async fetchUseFacility() {
      try {
        this.facilities = await facilityApi.get()
      } catch(err) {
        renderError(err)
      }
    },
    setDefaultDate() {
      const today = moment().format('YYYY-MM-DD')
      this.visit.use_datetime = today
    },
    async fetchToken() {
      try {
        const response = await memberApi.show(this.userId)
        this.token = response.token

        this.checkToken()
      } catch(err) {
        renderError(err)
      }
    },
    clearFacility() {
      this.visit.use_facility_id = 0
      this.visit.use_child_facility_id = 0
    },
    async onFacilityChange() {
      if (this.isFacilityLoading) {
        return false
      }

      this.isFacilityLoading = true

      this.visit.use_child_facility_id = 0

      if (this.visit.use_facility_id === 0) {
        this.isFacilityLoading = false
        return false
      }

      try {
        this.childFacilities = await facilityApi.getChildren(
          this.visit.use_facility_id
        )
        this.$nextTick(() => (this.isFacilityLoading = false))
      } catch(err) {
        this.isFacilityLoading = false
        // 無効な施設
        if (isNotFound(err)) {
          this.clearFacility()
          return false
        }
        renderError(err)
      }
    },
    /**
     * 検索条件を復元する
     */
    async restoreQuery() {
      try {
        const response = await receptionistApi.getCondition()

        // 無効な店舗
        if (this.isFacilityInvalid(response.use_facility_id)) {
          this.clearFacility()
          return false
        }

        this.visit.use_facility_id = response.use_facility_id

        await this.onFacilityChange()

        this.visit.use_child_facility_id = response.use_child_facility_id
      } catch(err) {
        renderError(err)
      }
    },
    isFacilityInvalid(id) {
      return !this.facilities.some(f => f.use_facility_id === id)
    },
    async submit() {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        const response = await visitApi.create(this.userId, this.visit)
        this.historyId = response.history_id
        this.$modal.show('complate-update')
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    },
    modalOk() {
      this.$modal.close('complate-update')

      this.$router.push({
        name: 'visit.show',
        params: {
          visitorId: this.userId,
          historyId: this.historyId
        }
      })
    },
    checkToken() {
      if (this.$route.query.token !== this.token) {
        visitTokenMismatch()
      }
    }
  },

  created() {
    this.$withLoading(async () => {
      await this.fetchToken()

      await Promise.all([
        this.fetchVisit(),
        this.fetchUseFacility(),
        this.fetchUsePurpose(),
      ])
      this.setDefaultDate()
      this.restoreQuery()
    })
  },

  watch: {
    loading(val) {
      this.canSubmit = !val
    }
  }
}
</script>
